import * as React from 'react';
import { Check, ChevronDown, Plus, Search, Undo2 } from 'lucide-react';
import { Badge } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import { Card } from '@ooh/ui/components/card';
import { Input } from '@ooh/ui/components/input';
import { cn } from '@ooh/ui/lib/utils';
import type { QuickAddOfferDraftOption } from './quick-add-offer-picker';

function OfferStatusBadge({ label }: { label?: string }) {
  if (!label) return null;

  return (
    <Badge variant="warning" className="h-5 shrink-0 border-0 px-2 py-0 text-xs font-medium">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-status-option" />
      {label}
    </Badge>
  );
}

export function SelectedOfferCard({
  draft,
  draftUnitLabel,
  draftSummarySeparator,
  pendingUnitsLabel,
  changeSelectionLabel,
  locked = false,
  onChange,
}: {
  draft: QuickAddOfferDraftOption;
  draftUnitLabel: string;
  draftSummarySeparator: string;
  /** Locked-target summary prefix. Only read when `locked` is true. */
  pendingUnitsLabel: string;
  changeSelectionLabel: string;
  /** Locked target: the destination cannot be changed, so no "Zmień" affordance. */
  locked?: boolean;
  onChange: () => void;
}) {
  return (
    <Card
      role={locked ? undefined : 'button'}
      tabIndex={locked ? undefined : 0}
      onClick={locked ? undefined : onChange}
      onKeyDown={
        locked
          ? undefined
          : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange();
              }
            }
      }
      className={cn(
        'flex items-center gap-3 rounded-xl border-2 border-border bg-background px-4 py-3 transition-colors',
        !locked &&
          'cursor-pointer hover:border-foreground/40 hover:bg-muted/30 focus-visible:border-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold leading-tight text-foreground">
            {draft.title}
          </h3>
          <OfferStatusBadge label={draft.statusLabel} />
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {locked ? (
            <>
              {pendingUnitsLabel}: {draft.unitCount} {draftUnitLabel}
            </>
          ) : (
            <>
              {draft.clientName} {draftSummarySeparator} {draft.unitCount} {draftUnitLabel}
            </>
          )}
        </p>
      </div>
      {locked ? null : (
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
          {changeSelectionLabel}
          <ChevronDown className="h-3 w-3" />
        </span>
      )}
    </Card>
  );
}

function DraftOption({
  draft,
  selected,
  draftUnitLabel,
  draftSummarySeparator,
  onSelect,
}: {
  draft: QuickAddOfferDraftOption;
  selected: boolean;
  draftUnitLabel: string;
  draftSummarySeparator: string;
  onSelect: (draftId: string) => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      fullWidth
      data-testid={`quick-add-draft-${draft.id}`}
      className={cn(
        'h-auto justify-start rounded-none border-t border-border/50 px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-muted/20',
        selected && 'bg-muted/20',
      )}
      onClick={() => onSelect(draft.id)}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{draft.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {draft.clientName} {draftSummarySeparator} {draft.unitCount} {draftUnitLabel}
        </div>
      </div>
      <div className="flex items-center gap-2.5">
        <OfferStatusBadge label={draft.statusLabel} />
        {selected ? <Check className="h-4 w-4 text-primary" /> : null}
      </div>
    </Button>
  );
}
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  );
}

export function CreateNewLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto gap-1.5 px-0 py-0 text-sm font-medium text-destructive hover:bg-transparent hover:text-destructive/90"
      onClick={onClick}
    >
      <Plus className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

function filterDraftsByQuery(
  drafts: QuickAddOfferDraftOption[],
  query: string,
): QuickAddOfferDraftOption[] {
  const normalized = query.trim().toLowerCase();
  if (normalized === '') return drafts;
  return drafts.filter(
    (draft) =>
      draft.title.toLowerCase().includes(normalized) ||
      draft.clientName.toLowerCase().includes(normalized),
  );
}

export function OfferList({
  drafts,
  selectedDraftId,
  draftUnitLabel,
  draftSummarySeparator,
  targetOfferLabel,
  searchPlaceholder,
  createNewLinkLabel,
  cancelChangeLabel,
  noDraftsLabel,
  noPermissionLabel,
  noResultsLabel,
  onSelectDraft,
  onCreateNew,
  onCancelChange,
}: {
  drafts: QuickAddOfferDraftOption[];
  selectedDraftId: string | null;
  draftUnitLabel: string;
  draftSummarySeparator: string;
  targetOfferLabel: string;
  searchPlaceholder: string;
  createNewLinkLabel: string;
  cancelChangeLabel: string;
  noDraftsLabel: string;
  noPermissionLabel: string;
  noResultsLabel: string;
  onSelectDraft: (draftId: string) => void;
  onCreateNew: () => void;
  onCancelChange: () => void;
}) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const editableDrafts = drafts.filter((draft) => draft.editable);
  const visibleDrafts = filterDraftsByQuery(editableDrafts, searchQuery);

  if (drafts.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
          {noDraftsLabel}
        </div>
        <CreateNewLink label={createNewLinkLabel} onClick={onCreateNew} />
      </div>
    );
  }

  if (editableDrafts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
        {noPermissionLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>{targetOfferLabel}</SectionLabel>
        <CreateNewLink label={createNewLinkLabel} onClick={onCreateNew} />
      </div>

      <div className="rounded-xl border-2 border-border bg-background">
        <div className="border-b border-border/50 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-10 rounded-xl pl-10 text-sm"
              data-testid="quick-add-offer-search"
            />
          </div>
        </div>

        <div>
          {visibleDrafts.length === 0 ? (
            <div className="px-4 py-4 text-sm text-muted-foreground">{noResultsLabel}</div>
          ) : (
            visibleDrafts.map((draft) => (
              <DraftOption
                key={draft.id}
                draft={draft}
                selected={draft.id === selectedDraftId}
                draftUnitLabel={draftUnitLabel}
                draftSummarySeparator={draftSummarySeparator}
                onSelect={onSelectDraft}
              />
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border/50 px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            className="h-auto gap-1.5 px-0 py-0 text-xs text-muted-foreground"
            onClick={onCancelChange}
          >
            <Undo2 className="h-3.5 w-3.5" />
            {cancelChangeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
